// src/modules/leads/route-planner/RoutePlannerMap.js
import React, { useEffect, useRef, useCallback, useState } from 'react';
import { PlannerMapContainer, LoadingOverlay, Spinner } from './routePlannerStyles';
import { createNumberedPin, createDotPin, darkMapStyles, MARKER_COLORS } from './routeUtils';
import formatPhone from '../../../components/shared/formatPhone';
import { getDirectCallKey, formatPhoneNumber } from '../../../services/communicationService';
import FeedbackModal from '../../../components/shared/ModalNotification';

const RoutePlannerMap = ({
  isGoogleLoaded,
  departure,
  destination,
  mappableLeads,
  selectedLeadIds,
  routeResponse,
  baseRouteResponse,
  routeStops,
  alongRouteLeads,
  showAlongRoute,
  isOptimizing,
  onToggleLead,
  onAddAlongRouteLead,
  highlightedLeadId,
}) => {
  const mapRef = useRef(null);
  const googleMapRef = useRef(null);
  const markersRef = useRef([]);
  const directionsRendererRef = useRef(null);
  const baseRendererRef = useRef(null);
  const infoWindowRef = useRef(null);
  const directCallKeyRef = useRef('');
  // Tracks the set of points already auto-fit on screen, so we only refit the
  // viewport when the actual leads/endpoints change — not when the user merely
  // selects/deselects a pin (which would otherwise snap the zoom back).
  const lastFitKeyRef = useRef('');

  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackSuccess, setFeedbackSuccess] = useState(true);

  // Pre-fetch direct-call key so the copy click handler can write to the
  // clipboard synchronously — Safari/strict-Chrome drop the user-gesture
  // permission across an awaited fetch, blocking clipboard.writeText.
  useEffect(() => {
    getDirectCallKey()
      .then(({ key }) => { directCallKeyRef.current = key; })
      .catch(err => console.error('Failed to prefetch direct-call key:', err));
  }, []);

  // ─── Initialize Map ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!isGoogleLoaded || !mapRef.current || googleMapRef.current) return;

    const defaultCenter = { lat: 42.3601, lng: -71.0589 }; // Boston area default
    googleMapRef.current = new window.google.maps.Map(mapRef.current, {
      zoom: 10,
      center: departure ? { lat: departure.lat, lng: departure.lng } : defaultCenter,
      styles: darkMapStyles,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: true,
      zoomControl: true,
      gestureHandling: 'greedy', // Better for mobile
    });

    infoWindowRef.current = new window.google.maps.InfoWindow();
  }, [isGoogleLoaded, departure]);

  // ─── Clear all markers ──────────────────────────────────────────────────
  const clearMarkers = useCallback(() => {
    markersRef.current.forEach(m => {
      window.google?.maps?.event?.clearInstanceListeners(m);
      m.setMap(null);
    });
    markersRef.current = [];
    if (infoWindowRef.current) {
      infoWindowRef.current.close();
    }
  }, []);

  // ─── Clear directions renderers ─────────────────────────────────────────
  const clearDirections = useCallback(() => {
    if (directionsRendererRef.current) {
      directionsRendererRef.current.setMap(null);
      directionsRendererRef.current = null;
    }
    if (baseRendererRef.current) {
      baseRendererRef.current.setMap(null);
      baseRendererRef.current = null;
    }
  }, []);

  // ─── Render route lines ────────────────────────────────────────────────
  useEffect(() => {
    if (!googleMapRef.current || !isGoogleLoaded) return;

    clearDirections();

    // Show the optimized route if available
    if (routeResponse) {
      directionsRendererRef.current = new window.google.maps.DirectionsRenderer({
        map: googleMapRef.current,
        directions: routeResponse,
        suppressMarkers: true,
        polylineOptions: {
          strokeColor: MARKER_COLORS.waypoint,
          strokeOpacity: 0.8,
          strokeWeight: 5,
        },
        preserveViewport: false,
      });
    } else if (baseRouteResponse) {
      // Show the base A→B route as a dashed/lighter line
      baseRendererRef.current = new window.google.maps.DirectionsRenderer({
        map: googleMapRef.current,
        directions: baseRouteResponse,
        suppressMarkers: true,
        polylineOptions: {
          strokeColor: MARKER_COLORS.waypoint,
          strokeOpacity: 0.35,
          strokeWeight: 4,
        },
        preserveViewport: false,
      });
    }
  }, [routeResponse, baseRouteResponse, isGoogleLoaded, clearDirections]);

  // ─── Build info window HTML ────────────────────────────────────────────
  const buildInfoContent = useCallback((lead, isSelected, isAlongRoute) => {
    const statusName = lead.status?.status_name || lead.serviceRequests?.[0]?.LeadStatus?.status_name || 'No Status';
    const statusColor = lead.status?.color || '#95a5a6';
    const address = [lead.street, lead.city, lead.state, lead.zipcode].filter(Boolean).join(', ');
    const phone = formatPhone(lead.phone) || 'N/A';

    const buttonLabel = isSelected ? 'Remove from Route' : 'Add to Route';
    const buttonColor = isSelected ? '#e74c3c' : '#F69620';
    const buttonAction = isAlongRoute ? 'alongRoute' : 'toggle';

    const leadUrl = `${window.location.origin}/leads/view/${lead.id}`;

    return `
      <div style="padding:8px;min-width:240px;font-family:'Roboto',sans-serif;">
        <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:6px;gap:6px;">
          <a href="${leadUrl}" target="_blank" rel="noopener noreferrer"
             style="margin:0;font-size:14px;color:#0066cc;text-decoration:none;font-weight:600;flex:1;">
            ${lead.name}
          </a>
          <div style="display:flex;align-items:center;gap:6px;">
            <button onclick="window.__routePlannerShare__(${lead.id})" title="Copy lead info"
              style="background:none;border:none;cursor:pointer;font-size:14px;padding:2px 4px;color:#555;line-height:1;">
              &#128203;
            </button>
            <span style="padding:2px 8px;border-radius:10px;font-size:10px;background:${statusColor}22;color:${statusColor};">${statusName}</span>
          </div>
        </div>
        <p style="margin:3px 0;font-size:12px;color:#555;"><strong>Phone:</strong> ${phone}</p>
        <p style="margin:3px 0;font-size:12px;color:#555;"><strong>Address:</strong> ${address}</p>
        <button onclick="window.__routePlannerAction__('${buttonAction}', ${lead.id})"
          style="display:inline-block;margin-top:8px;padding:6px 14px;background:${buttonColor};color:#fff;border:none;border-radius:4px;font-size:12px;cursor:pointer;font-weight:600;">
          ${buttonLabel}
        </button>
      </div>
    `;
  }, []);

  // Build the WhatsApp-formatted share message for a lead (emoji icons +
  // click-to-call link with `from=` left empty for the user to fill in after pasting).
  // Synchronous — uses the pre-fetched direct-call key so the click handler can
  // call clipboard.writeText without awaiting (preserves user-gesture permission).
  const buildShareMessage = useCallback((lead) => {
    const fullAddress = [lead.street, lead.city, lead.state, lead.zipcode].filter(Boolean).join(', ');
    const phoneFormatted = formatPhone(lead.phone) || '';
    const leadUrl = `${window.location.origin}/leads/view/${lead.id}`;

    const key = directCallKeyRef.current;
    let directCallUrl = '';
    if (key && lead.phone) {
      const toParam = encodeURIComponent(formatPhoneNumber(lead.phone));
      directCallUrl = `https://crm.mrpatchasphalt.com/direct-call?to=${toParam}&leadId=${encodeURIComponent(lead.id)}&k=${encodeURIComponent(key)}&from=`;
    }

    return [
      `\uD83D\uDC64 ${lead.name || ''}`,               // 👤 name
      fullAddress ? `\uD83D\uDCCD Address: ${fullAddress}` : '',  // 📍
      phoneFormatted ? `\uD83D\uDCDE Phone: ${phoneFormatted}` : '', // 📞
      directCallUrl ? `\u260E\uFE0F Call: ${directCallUrl}` : '',  // ☎️
      `\uD83D\uDD17 Lead Details: ${leadUrl}`,         // 🔗
    ].filter(Boolean).join('\n');
  }, []);

  // ─── Set up global action handler for InfoWindow buttons ────────────────
  useEffect(() => {
    window.__routePlannerAction__ = (action, leadId) => {
      if (action === 'alongRoute' && onAddAlongRouteLead) {
        onAddAlongRouteLead(leadId);
      } else if (onToggleLead) {
        onToggleLead(leadId);
      }
      if (infoWindowRef.current) {
        infoWindowRef.current.close();
      }
    };

    window.__routePlannerShare__ = (leadId) => {
      const lead = [...mappableLeads, ...alongRouteLeads, ...routeStops].find(l => l.id === leadId);
      if (!lead) return;

      const message = buildShareMessage(lead);

      const showCopied = () => {
        setFeedbackMessage('Lead info copied to clipboard');
        setFeedbackSuccess(true);
        setShowFeedback(true);
      };

      const showError = () => {
        setFeedbackMessage('Failed to copy to clipboard');
        setFeedbackSuccess(false);
        setShowFeedback(true);
      };

      const fallbackCopy = () => {
        const ta = document.createElement('textarea');
        ta.value = message;
        ta.setAttribute('readonly', '');
        ta.style.position = 'absolute';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        let ok = false;
        try { ok = document.execCommand('copy'); } catch { ok = false; }
        document.body.removeChild(ta);
        if (ok) showCopied(); else showError();
      };

      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(message).then(showCopied).catch(fallbackCopy);
      } else {
        fallbackCopy();
      }
    };

    return () => {
      delete window.__routePlannerAction__;
      delete window.__routePlannerShare__;
    };
  }, [onToggleLead, onAddAlongRouteLead, mappableLeads, alongRouteLeads, routeStops, buildShareMessage]);

  // ─── Update markers ────────────────────────────────────────────────────
  useEffect(() => {
    if (!googleMapRef.current || !isGoogleLoaded) return;

    clearMarkers();

    const bounds = new window.google.maps.LatLngBounds();
    const newMarkers = [];

    // ── Departure marker ──
    if (departure) {
      const pos = { lat: departure.lat, lng: departure.lng };
      const marker = new window.google.maps.Marker({
        position: pos,
        map: googleMapRef.current,
        title: 'Start',
        icon: {
          url: createNumberedPin(MARKER_COLORS.departure, 'A'),
          scaledSize: new window.google.maps.Size(32, 44),
          anchor: new window.google.maps.Point(16, 44),
        },
        zIndex: 1000,
      });
      marker.addListener('click', () => {
        infoWindowRef.current.setContent(`
          <div style="padding:8px;font-family:'Roboto',sans-serif;">
            <h4 style="margin:0 0 4px;font-size:14px;color:#222;">Start Point</h4>
            <p style="margin:0;font-size:12px;color:#555;">${departure.address}</p>
          </div>
        `);
        infoWindowRef.current.open(googleMapRef.current, marker);
      });
      newMarkers.push(marker);
      bounds.extend(pos);
    }

    // ── Destination marker ──
    if (destination) {
      const pos = { lat: destination.lat, lng: destination.lng };
      const marker = new window.google.maps.Marker({
        position: pos,
        map: googleMapRef.current,
        title: 'Destination',
        icon: {
          url: createNumberedPin(MARKER_COLORS.destination, 'X'),
          scaledSize: new window.google.maps.Size(32, 44),
          anchor: new window.google.maps.Point(16, 44),
        },
        zIndex: 1000,
      });
      marker.addListener('click', () => {
        infoWindowRef.current.setContent(`
          <div style="padding:8px;font-family:'Roboto',sans-serif;">
            <h4 style="margin:0 0 4px;font-size:14px;color:#222;">Destination</h4>
            <p style="margin:0;font-size:12px;color:#555;">${destination.address}</p>
          </div>
        `);
        infoWindowRef.current.open(googleMapRef.current, marker);
      });
      newMarkers.push(marker);
      bounds.extend(pos);
    }

    // ── Route stop markers (numbered) ──
    if (routeStops.length > 0) {
      routeStops.forEach((lead, index) => {
        const pos = { lat: parseFloat(lead.latitude), lng: parseFloat(lead.longitude) };
        const marker = new window.google.maps.Marker({
          position: pos,
          map: googleMapRef.current,
          title: lead.name,
          icon: {
            url: createNumberedPin(MARKER_COLORS.waypoint, String(index + 1)),
            scaledSize: new window.google.maps.Size(32, 44),
            anchor: new window.google.maps.Point(16, 44),
          },
          zIndex: 900,
        });
        marker.addListener('click', () => {
          infoWindowRef.current.setContent(buildInfoContent(lead, true, false));
          infoWindowRef.current.open(googleMapRef.current, marker);
        });
        newMarkers.push(marker);
        bounds.extend(pos);
      });
    } else {
      // ── Unselected lead markers (when no route yet) ──
      mappableLeads.forEach(lead => {
        const isSelected = selectedLeadIds.has(lead.id);
        const pos = { lat: parseFloat(lead.latitude), lng: parseFloat(lead.longitude) };
        const color = isSelected ? MARKER_COLORS.waypoint : MARKER_COLORS.unselected;

        const marker = new window.google.maps.Marker({
          position: pos,
          map: googleMapRef.current,
          title: lead.name,
          icon: isSelected ? {
            url: createNumberedPin(color, '+'),
            scaledSize: new window.google.maps.Size(32, 44),
            anchor: new window.google.maps.Point(16, 44),
          } : {
            url: createDotPin(color, 0.5),
            scaledSize: new window.google.maps.Size(20, 28),
            anchor: new window.google.maps.Point(10, 28),
          },
          zIndex: isSelected ? 800 : 100,
        });

        marker.addListener('click', () => {
          infoWindowRef.current.setContent(buildInfoContent(lead, isSelected, false));
          infoWindowRef.current.open(googleMapRef.current, marker);
        });

        newMarkers.push(marker);
        bounds.extend(pos);
      });
    }

    // ── Along-route markers ──
    if (showAlongRoute && alongRouteLeads.length > 0) {
      alongRouteLeads.forEach(lead => {
        const pos = { lat: parseFloat(lead.latitude), lng: parseFloat(lead.longitude) };
        const marker = new window.google.maps.Marker({
          position: pos,
          map: googleMapRef.current,
          title: lead.name,
          icon: {
            url: createDotPin(MARKER_COLORS.alongRoute, 0.8),
            scaledSize: new window.google.maps.Size(24, 34),
            anchor: new window.google.maps.Point(12, 34),
          },
          zIndex: 500,
          animation: window.google.maps.Animation.DROP,
        });

        marker.addListener('click', () => {
          infoWindowRef.current.setContent(buildInfoContent(lead, false, true));
          infoWindowRef.current.open(googleMapRef.current, marker);
        });

        newMarkers.push(marker);
      });
    }

    markersRef.current = newMarkers;

    // Build a key describing which points are on the map (endpoints + leads),
    // independent of the current selection. We only auto-fit the viewport when
    // this key changes — so selecting/deselecting a lead keeps the user's zoom.
    const fitKey = [
      departure ? `${departure.lat},${departure.lng}` : '',
      destination ? `${destination.lat},${destination.lng}` : '',
      routeStops.length > 0
        ? `stops:${routeStops.map(l => l.id).join('-')}`
        : `leads:${mappableLeads.map(l => l.id).join('-')}`,
    ].join('|');
    const shouldFit = fitKey !== lastFitKeyRef.current;

    // Fit bounds only when the set of points changed (and no route drawn —
    // the route renderer handles its own viewport).
    if (shouldFit && !routeResponse && newMarkers.length > 1) {
      googleMapRef.current.fitBounds(bounds, { padding: 50 });
      lastFitKeyRef.current = fitKey;
    } else if (shouldFit && !routeResponse && newMarkers.length === 1) {
      googleMapRef.current.setCenter(bounds.getCenter());
      googleMapRef.current.setZoom(13);
      lastFitKeyRef.current = fitKey;
    }

  }, [
    isGoogleLoaded, departure, destination, mappableLeads, selectedLeadIds,
    routeStops, routeResponse, alongRouteLeads, showAlongRoute,
    clearMarkers, buildInfoContent, highlightedLeadId,
  ]);

  // ─── Pan to highlighted lead ────────────────────────────────────────────
  useEffect(() => {
    if (!highlightedLeadId || !googleMapRef.current) return;

    // Find the lead in all available data
    const lead = [...mappableLeads, ...alongRouteLeads].find(l => l.id === highlightedLeadId);
    if (lead?.latitude && lead?.longitude) {
      googleMapRef.current.panTo({
        lat: parseFloat(lead.latitude),
        lng: parseFloat(lead.longitude),
      });
      if (!routeResponse) {
        googleMapRef.current.setZoom(14);
      }
    }
  }, [highlightedLeadId, mappableLeads, alongRouteLeads, routeResponse]);

  return (
    <PlannerMapContainer>
      <div ref={mapRef} style={{ width: '100%', height: '100%' }} />
      {isOptimizing && (
        <LoadingOverlay>
          <Spinner />
        </LoadingOverlay>
      )}
      <FeedbackModal
        isOpen={showFeedback}
        message={feedbackMessage}
        success={feedbackSuccess}
        onComplete={() => setShowFeedback(false)}
      />
    </PlannerMapContainer>
  );
};

export default RoutePlannerMap;
